import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ButtonsBlockDesktop,
    HeaderContent,
    HeaderLeft,
    HeaderRight,
    HeaderWrapper,
    MenuIconMobile,
    MobileSearchButton,
    VerticalDivider,
} from './HeaderStyled';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, IconButton, Typography } from '@mui/material';
import { HT_DESKTOP_HEADER_HEIGHT } from '@/lib/constants/hyperTrackerConstants';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectMemberProfile } from '@/state/member/memberSelectors';
import { DefaultIconButton } from '@/components/Header/HeaderStyled';
import _UserIcon from '@/assets/images/icons/hl-user.svg?react';
import HTProfileMenu from '@/components/Hypertracker/Header/HTProfileMenu';
import HTAuthMenu from '@/components/Hypertracker/Header/HTProfileMenu/HTAuthMenu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';
import { selectIsAsideMenu, selectIsHyperSearchMenu } from '@/state/general/generalSelectors';
import {
    setIsAsideMenu,
    setIsHyperSearch,
    setIsTryingToClaimWallet,
} from '@/state/general/generalSlice';
import SecondaryHeader from '@/components/Hypertracker/SecondaryHeader';
import HyperSearch from '@/components/Hypertracker/HyperSearch';
import SearchIcon from '@mui/icons-material/Search';
import { resetModalWindow } from '@/state/hypertracker/hypertackerSlice';
import MobileHeader from '@/components/Hypertracker/Header/MobileHeader';
import useSegmentsData from '@/hooks/useSegmentsData';
import LanguageIcon from '@mui/icons-material/Language';
import LanguageMenu from '@/components/Hypertracker/Header/LanguageMenu/LanguageMenu';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import useWidgetsData from '@/pages/HypertrackerHome/useWidgetsData';

const UserIcon = styled(_UserIcon)(() => ({
    width: '32px',
    height: '32px',
}));

const Header: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
    const member = useSelector((state: RootState) => selectMemberProfile(state));
    const { id: userId } = member;
    const isAsideMenu = useSelector((state: RootState) => selectIsAsideMenu(state));
    const { segmentsSummaryData } = useSegmentsData();
    const isHyperSearchMenu = useSelector((state: RootState) => selectIsHyperSearchMenu(state));
    const isMemberAuthorized = useIsMemberAuthorized();
    const { fetchClientStorage } = useWidgetsData();

    // Restore saved preferences (e.g. wallet display mode) for logged-in users on any page
    useEffect(() => {
        if (isMemberAuthorized) {
            fetchClientStorage();
        }
    }, [isMemberAuthorized]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
        setLangAnchorEl(event.currentTarget);
    };

    const handleLangClose = () => {
        setLangAnchorEl(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const avatarLetter = useMemo(() => {
        if (member?.firstName) {
            return member.firstName[0].toUpperCase();
        } else if (member?.username) {
            return member.username[0].toUpperCase();
        } else return '';
    }, [member]);

    const handleAsideMenuToggle = () => {
        dispatch(setIsAsideMenu(!isAsideMenu));
        dispatch(resetModalWindow());
        dispatch(setIsTryingToClaimWallet(false));
    };

    const handleHyperSearchMenuToggle = () => {
        dispatch(setIsHyperSearch(!isHyperSearchMenu));
        dispatch(resetModalWindow());
        dispatch(setIsTryingToClaimWallet(false));
    };

    if (isMobile) {
        return <MobileHeader />;
    }

    return (
        <HeaderWrapper
            data-testid="header"
            sx={{
                height: `${HT_DESKTOP_HEADER_HEIGHT}px`,
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 1000,
                borderStyle: 'solid',
                borderColor: (theme) => theme.palette.divider,
                borderBottomWidth: '1px',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <HeaderContent sx={{ height: 'unset' }}>
                <HeaderLeft sx={{ width: '100%', padding: 0 }}>
                    <SecondaryHeader segmentsData={segmentsSummaryData} />
                </HeaderLeft>
                <HeaderRight>
                    <HyperSearch id={'search-open'} />
                    <VerticalDivider />
                    {!isMobile && (
                        <ButtonsBlockDesktop>
                            <IconButton
                                size={'small'}
                                onClick={handleLangClick}
                                data-testid="language-button"
                                aria-label="Language"
                            >
                                <LanguageIcon
                                    sx={{
                                        width: '18px',
                                        height: '18px',
                                        color: (theme) => theme.palette.customText.text1,
                                    }}
                                />
                            </IconButton>
                            <VerticalDivider />
                            {userId ? (
                                <DefaultIconButton onClick={handleClick} data-testid="avatar">
                                    <Avatar
                                        sx={{
                                            width: '24px',
                                            height: '24px',
                                            backgroundColor: theme.palette.hyperLiquidColor,
                                            color: 'common.white',
                                            fontWeight: 700,
                                            fontSize: '14px',
                                        }}
                                    >
                                        {avatarLetter}
                                    </Avatar>
                                </DefaultIconButton>
                            ) : (
                                <DefaultIconButton
                                    onClick={handleClick}
                                    data-testid="avatar"
                                    sx={{
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            background: 'transparent',
                                        },
                                    }}
                                >
                                    <UserIcon />
                                </DefaultIconButton>
                            )}
                        </ButtonsBlockDesktop>
                    )}
                    <MobileSearchButton onClick={handleHyperSearchMenuToggle}>
                        <SearchIcon sx={{ height: '16px', width: 'auto' }} />
                        <Typography variant={'p12'} color={'common.white'}>
                            {t('hypersearch.title')}
                        </Typography>
                    </MobileSearchButton>
                    {isHyperSearchMenu ? (
                        <MenuIconMobile onClick={handleHyperSearchMenuToggle}>
                            <CloseIcon />
                        </MenuIconMobile>
                    ) : (
                        <MenuIconMobile onClick={handleAsideMenuToggle}>
                            {isAsideMenu ? <CloseIcon /> : <MenuIcon />}
                        </MenuIconMobile>
                    )}
                </HeaderRight>
            </HeaderContent>
            {userId ? (
                <HTProfileMenu
                    anchorEl={anchorEl}
                    handleClose={handleClose}
                    open={Boolean(anchorEl)}
                    avatarLetter={avatarLetter}
                />
            ) : (
                <HTAuthMenu
                    anchorEl={anchorEl}
                    handleClose={handleClose}
                    open={Boolean(anchorEl)}
                />
            )}
            <LanguageMenu
                anchorEl={langAnchorEl}
                handleClose={handleLangClose}
                open={Boolean(langAnchorEl)}
            />
        </HeaderWrapper>
    );
};

export default Header;
